/**
 * ============================================================================
 * AcceptInvitePage - Organizer Invitation Acceptance
 * ============================================================================
 * Page for organizers to accept their invitation and set up their account
 * Accessed via link in invitation email: /accept-invite?token=xxx
 * 
 * Created: November 28, 2025 - Phase 2E Organization Management
 * Updated: December 8, 2025 - Added success confirmation screen with close button
 * Updated: January 28, 2026 - Added real-time password requirements feedback
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);  // NEW: Success state for confirmation screen
  
  // Form fields
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Validate token and get invitation details
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError('Invalid invitation link. Please check your email for the correct link.');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/auth/validate-invite?token=${token}`);
        const data = await response.json();

        if (!response.ok) {
          setError(data.error || 'This invitation link is invalid or has expired.');
          setLoading(false);
          return;
        }

        setInvitation(data.invitation);
        setLoading(false);
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Failed to validate invitation. Please try again.');
        setLoading(false);
      }
    };

    validateToken();
  }, [token]);

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/auth/accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          password
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to accept invitation.');
        setSubmitting(false);
        return;
      }

      // Store tokens
      if (data.accessToken) {
        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
      }

      // Show success confirmation screen (instead of alert)
      setSuccess(true);

    } catch (err) {
      console.error('Accept invite error:', err);
      setError('Failed to complete setup. Please try again.');
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48,
            height: 48,
            border: '4px solid rgba(255,255,255,0.3)',
            borderTopColor: '#fff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: '#fff', fontSize: 16 }}>Validating invitation...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Error state (invalid/expired token)
  if (error && !invitation) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 48,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}>
          <div style={{ fontSize: 64, marginBottom: 16 }}>⚠️</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#dc2626', marginBottom: 16 }}>
            Invalid Invitation
          </h1>
          <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 24 }}>
            {error}
          </p>
          <a
            href="/"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              background: '#2d6a4f',
              color: '#fff',
              borderRadius: 8,
              textDecoration: 'none',
              fontWeight: 600
            }}
          >
            Return to Home
          </a>
        </div>
      </div>
    );
  }

  // SUCCESS STATE - Show confirmation with button to continue
  if (success) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
        padding: 24
      }}>
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 48,
          maxWidth: 500,
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
        }}>
          <div style={{ 
            fontSize: 64, 
            marginBottom: 16,
            animation: 'bounce 0.5s ease-out'
          }}>✅</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: '#166534', marginBottom: 16 }}>
            You're All Set!
          </h1>
          <p style={{ color: '#374151', fontSize: 18, marginBottom: 8 }}>
            Welcome to <strong>{invitation?.organization_name}</strong>
          </p>
          <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 32 }}>
            Your account has been created successfully. You can now access your organizer dashboard to manage tee times, players, and more.
          </p>
          
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 18,
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: 12
            }}
          >
            🚀 Go to My Dashboard
          </button>
          
          <button
            onClick={() => window.close()}
            style={{
              width: '100%',
              padding: '12px 24px',
              background: '#f3f4f6',
              color: '#6b7280',
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            Close This Window
          </button>
        </div>
        
        <style>{`
          @keyframes bounce {
            0% { transform: scale(0); }
            50% { transform: scale(1.2); }
            100% { transform: scale(1); }
          }
        `}</style>
      </div>
    );
  }

  // Invitation form
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #2d6a4f, #1b4332)',
      padding: 24
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        padding: 48,
        maxWidth: 480,
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 80,
            height: 80,
            background: '#f0fdf4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
            fontSize: 40
          }}>
            ⛳
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1b4332', marginBottom: 8 }}>
            Welcome to Tee Time Organizer!
          </h1>
          <p style={{ color: '#6b7280', fontSize: 16, marginBottom: 8 }}>
            You've been invited to manage
          </p>
          <p style={{ 
            fontSize: 20, 
            fontWeight: 700, 
            color: '#2d6a4f',
            background: '#f0fdf4',
            padding: '8px 16px',
            borderRadius: 8,
            display: 'inline-block'
          }}>
            {invitation?.organization_name}
          </p>
        </div>

        {/* Welcome message */}
        <div style={{
          background: '#f0fdf4',
          borderRadius: 12,
          padding: 16,
          marginBottom: 24
        }}>
          <p style={{ color: '#166534', fontSize: 14, margin: 0 }}>
            <strong>Hi {invitation?.name}!</strong> Set up your password below to get started managing your golf club's tee times, players, and communications.
          </p>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 20,
            color: '#dc2626',
            fontSize: 14
          }}>
            {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 6
            }}>
              Your Email
            </label>
            <input
              type="email"
              value={invitation?.email || ''}
              disabled
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '1px solid #e5e7eb',
                fontSize: 16,
                background: '#f9fafb',
                color: '#6b7280',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 6
            }}>
              Create Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={8}
                style={{
                  width: '100%',
                  padding: '12px 48px 12px 16px',
                  borderRadius: 8,
                  border: `2px solid ${password.length > 0 && password.length < 8 ? '#fca5a5' : '#e5e7eb'}`,
                  fontSize: 16,
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: 12,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 18
                }}
              >
                {showPassword ? '🙈' : '👁️'}
              </button>
            </div>
            {/* Password requirements indicator */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 6, 
              marginTop: 6,
              fontSize: 12,
              color: password.length >= 8 ? '#16a34a' : (password.length > 0 ? '#dc2626' : '#6b7280')
            }}>
              <span>{password.length >= 8 ? '✓' : '○'}</span>
              <span>Minimum 8 characters {password.length > 0 && `(${password.length}/8)`}</span>
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={{
              display: 'block',
              fontSize: 14,
              fontWeight: 600,
              color: '#374151',
              marginBottom: 6
            }}>
              Confirm Password
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your password"
              required
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 8,
                border: '2px solid #e5e7eb',
                fontSize: 16,
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />
            {confirmPassword && password !== confirmPassword && (
              <p style={{ color: '#dc2626', fontSize: 12, marginTop: 4 }}>
                Passwords don't match
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting || password !== confirmPassword || password.length < 8}
            style={{
              width: '100%',
              padding: '14px 24px',
              background: (submitting || password !== confirmPassword || password.length < 8) 
                ? '#9ca3af' 
                : 'linear-gradient(135deg, #2d6a4f, #1b4332)',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 16,
              fontWeight: 600,
              cursor: (submitting || password !== confirmPassword || password.length < 8) 
                ? 'not-allowed' 
                : 'pointer'
            }}
          >
            {submitting ? 'Setting up your account...' : '✨ Complete Setup & Get Started'}
          </button>
        </form>

        {/* Footer */}
        <p style={{
          textAlign: 'center',
          color: '#9ca3af',
          fontSize: 12,
          marginTop: 24
        }}>
          By setting up your account, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}

export default AcceptInvitePage;
